import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock, ArrowLeft, ArrowRight, Building, Globe } from 'lucide-react';

interface SignupData {
  email: string;
  password: string;
  country: string;
  businessName: string;
  businessType: string;
}

const SignupPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { signUp } = useAuth();

  const [signupData, setSignupData] = useState<SignupData>({
    email: '',
    password: '',
    country: '',
    businessName: '',
    businessType: ''
  });

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 
    'France', 'Italy', 'Spain', 'Netherlands', 'Sweden', 'Norway', 
    'Denmark', 'Finland', 'Ireland', 'New Zealand', 'Singapore'
  ];

  const businessTypes = [
    'Dry Cleaning',
    'Laundry', 
    'Shoe Keeper / Cobblers',
    'Tailoring'
  ];

  const handleStep1Submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!signupData.email || !signupData.password || !signupData.country) {
      setError('Please fill in all fields');
      return;
    }

    if (signupData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setCurrentStep(2);
  };

  const handleStep2Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!signupData.businessName || !signupData.businessType) {
      setError('Please fill in all fields');
      setIsLoading(false);
      return;
    }

    try {
      // Use the AuthContext signup method
      const { error } = await signUp(signupData);

      if (error) {
        setError(error.message);
      } else {
        // Success - redirect to POS
        // Use full page navigation for cross-app routing (POS app is separate)
        console.log('✅ Signup successful, redirecting to POS...');
        window.location.href = '/pos';
      }
    } catch (err) {
      console.error('❌ Signup error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const goBackToStep1 = () => {
    setCurrentStep(1);
    setError('');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center">
          <img src="/src/assets/icons/smartdry-logo-final.svg" alt="SmartDry Logo" className="h-6 w-auto" />
        </div>
        
        {/* Title */}
        <h2 className="mt-8 text-center text-3xl font-bold text-gray-900">
          Sign up
        </h2>
        
        {/* Login link */}
        <p className="mt-4 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <a href="/dashboard/login" className="font-medium text-[#7de2c5] hover:text-[#6dd1b4]">
            Sign in
          </a>
        </p>

        {/* Progress indicator */}
        <div className="mt-6 flex items-center justify-center space-x-4">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            currentStep >= 1 ? 'bg-[#7de2c5] text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            1
          </div>
          <div className={`w-16 h-1 ${currentStep >= 2 ? 'bg-[#7de2c5]' : 'bg-gray-200'}`}></div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            currentStep >= 2 ? 'bg-[#7de2c5] text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            2
          </div>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
          {currentStep === 1 ? (
            /* Step 1: Account Details */
            <form className="space-y-6" onSubmit={handleStep1Submit}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7de2c5] focus:border-transparent sm:text-sm"
                    placeholder="Enter your email"
                    value={signupData.email}
                    onChange={(e) => setSignupData({...signupData, email: e.target.value})}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7de2c5] focus:border-transparent sm:text-sm"
                    placeholder="Create a password"
                    value={signupData.password}
                    onChange={(e) => setSignupData({...signupData, password: e.target.value})}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">Must be at least 6 characters</p>
              </div>

              {/* Country Field */}
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Globe className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    id="country"
                    name="country"
                    required
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7de2c5] focus:border-transparent sm:text-sm"
                    value={signupData.country}
                    onChange={(e) => setSignupData({...signupData, country: e.target.value})}
                  >
                    <option value="">Select your country</option>
                    {countries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Continue Button */}
              <div>
                <button
                  type="submit"
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#7de2c5] hover:bg-[#6dd1b4] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7de2c5] transition-colors"
                >
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              </div>
            </form>
          ) : (
            /* Step 2: Business Information */
            <form className="space-y-6" onSubmit={handleStep2Submit}>
              <div className="text-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">Business Information</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {signupData.email} <button onClick={goBackToStep1} className="text-[#7de2c5] hover:text-[#6dd1b4] underline">Change</button>
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              {/* Business Name Field */}
              <div>
                <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-2">
                  Business name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="businessName"
                    name="businessName"
                    type="text"
                    required
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7de2c5] focus:border-transparent sm:text-sm"
                    placeholder="Enter your business name"
                    value={signupData.businessName}
                    onChange={(e) => setSignupData({...signupData, businessName: e.target.value})}
                  />
                </div>
              </div>

              {/* Primary Business Type */}
              <div>
                <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Industry <span className="text-red-500">*</span>
                </label>
                <select
                  id="businessType"
                  name="businessType"
                  required
                  className="block w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7de2c5] focus:border-transparent sm:text-sm"
                  value={signupData.businessType}
                  onChange={(e) => setSignupData({...signupData, businessType: e.target.value})}
                >
                  <option value="">Select your primary industry</option>
                  {businessTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={goBackToStep1}
                  className="flex-1 flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7de2c5] transition-colors"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#7de2c5] hover:bg-[#6dd1b4] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7de2c5] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </div>
                  ) : (
                    'Create your account'
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Terms and Conditions */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              By signing up, you agree to our{' '}
              <a href="#" className="text-[#7de2c5] hover:text-[#6dd1b4]">Terms of Service</a>{' '}
              and{' '}
              <a href="#" className="text-[#7de2c5] hover:text-[#6dd1b4]">Privacy Policy</a>.
            </p>
          </div>

          {/* Clear form link */}
          {currentStep === 2 && (
            <div className="mt-4 text-center">
              <button 
                onClick={() => {
                  setSignupData({
                    email: '',
                    password: '',
                    country: '',
                    businessName: '',
                    businessType: ''
                  });
                  setCurrentStep(1);
                  setError('');
                }}
                className="text-xs text-[#7de2c5] hover:text-[#6dd1b4] underline"
              >
                Clear form and try again
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            © 2024 SmartDry POS. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
